import { supabase } from '../lib/supabase'
import { monthRange, effectiveDateFilter } from '../lib/dateUtils'
import { projectRecurringRowInRange, type CardCycle } from '../lib/recurringProjection'
import type { Category, AnnualMonth, AnnualCategoryRow, MonthSummary } from '../types'

type RowWithCard = { card_id?: string | null }

async function fetchCardCyclesForRows(householdId: string, rows: RowWithCard[]): Promise<Map<string, CardCycle>> {
  if (!rows.some(row => row.card_id)) return new Map()

  const { data, error } = await supabase
    .from('cards')
    .select('id, close, due')
    .eq('household_id', householdId)
  if (error) throw error

  const cycles = new Map<string, CardCycle>()
  for (const card of data ?? []) {
    cycles.set(card.id, { close: Number(card.close), due: Number(card.due) })
  }
  return cycles
}

export async function getMonthSummary(
  householdId: string,
  month: number,
  year: number,
): Promise<MonthSummary> {
  const { from, to } = monthRange(month, year)

  const [{ data, error }, { data: recurringData, error: recurringError }] = await Promise.all([
    supabase
      .from('transactions')
      .select('kind, amount, realized')
      .eq('household_id', householdId)
      .or(effectiveDateFilter(from, to)),
    supabase
      .from('transactions')
      .select('kind, amount, date, card_id, due_date, recurring_until')
      .eq('household_id', householdId)
      .eq('recurring', true)
      .lt('date', from),
  ])

  if (error) throw error
  if (recurringError) throw recurringError

  const cardCycles = await fetchCardCyclesForRows(householdId, recurringData ?? [])
  const activeRecurring = (recurringData ?? []).flatMap(row => {
    const cycle = row.card_id ? cardCycles.get(row.card_id) : undefined
    return projectRecurringRowInRange(row, from, to, cycle)
  })

  let realizedIncome = 0
  let projectedIncome = 0
  let realizedExpense = 0
  let projectedExpense = 0

  for (const row of [...(data ?? []), ...activeRecurring]) {
    const isRealized = (row as { realized?: boolean }).realized ?? false
    const amount = Number(row.amount)
    if (row.kind === 'in') {
      projectedIncome += amount
      if (isRealized) realizedIncome += amount
    } else if (row.kind === 'out') {
      projectedExpense += amount
      if (isRealized) realizedExpense += amount
    }
  }

  const realizedBalance = realizedIncome - realizedExpense
  const projectedBalance = projectedIncome - projectedExpense
  const realizedPct = realizedIncome > 0 ? Math.round((realizedExpense / realizedIncome) * 100) : 0
  const projectedPct = projectedIncome > 0 ? Math.round((projectedExpense / projectedIncome) * 100) : 0

  return {
    realizedIncome,
    projectedIncome,
    realizedExpense,
    projectedExpense,
    realizedBalance,
    projectedBalance,
    realizedPct,
    projectedPct,
  }
}

export async function getCategoryBreakdown(
  householdId: string,
  month: number,
  year: number,
): Promise<Category[]> {
  const { from, to } = monthRange(month, year)

  const [{ data, error }, { data: recurringData, error: recurringError }] = await Promise.all([
    supabase
      .from('transactions')
      .select('amount, categories!category_id(id, name, kind, spending_limit)')
      .eq('household_id', householdId)
      .eq('kind', 'out')
      .or(effectiveDateFilter(from, to)),
    supabase
      .from('transactions')
      .select('amount, date, card_id, due_date, categories!category_id(id, name, kind, spending_limit), recurring_until')
      .eq('household_id', householdId)
      .eq('kind', 'out')
      .eq('recurring', true)
      .lt('date', from),
  ])

  if (error) throw error
  if (recurringError) throw recurringError

  const cardCycles = await fetchCardCyclesForRows(householdId, recurringData ?? [])
  const activeRecurring = (recurringData ?? []).flatMap(row => {
    const cycle = row.card_id ? cardCycles.get(row.card_id) : undefined
    return projectRecurringRowInRange(row, from, to, cycle)
  })

  const map = new Map<string, { name: string; kind: 'in' | 'out'; amount: number; spending_limit: number | null }>()

  for (const row of [...(data ?? []), ...activeRecurring]) {
    const cat = row.categories as { id?: string; name?: string; kind?: string; spending_limit?: number | null } | null
    if (!cat?.id || !cat.name) continue
    const existing = map.get(cat.id)
    if (existing) {
      existing.amount += Number(row.amount)
    } else {
      map.set(cat.id, {
        name: cat.name,
        kind: (cat.kind ?? 'out') as 'in' | 'out',
        amount: Number(row.amount),
        spending_limit: cat.spending_limit ?? null,
      })
    }
  }

  return Array.from(map.entries())
    .map(([id, val]) => ({ id, ...val }))
    .sort((a, b) => b.amount - a.amount)
}

export async function getAnnualData(
  householdId: string,
  year: number,
): Promise<AnnualMonth[]> {
  const from = `${year}-01-01`
  const to   = `${year + 1}-01-01`

  const [{ data, error }, { data: recurringPrev, error: recurringError }] = await Promise.all([
    supabase
      .from('transactions')
      .select('kind, amount, date, due_date, card_id, recurring, recurring_until')
      .eq('household_id', householdId)
      .or(effectiveDateFilter(from, to)),
    supabase
      .from('transactions')
      .select('kind, amount, date, card_id, due_date, recurring_until')
      .eq('household_id', householdId)
      .eq('recurring', true)
      .lt('date', from),
  ])

  if (error) throw error
  if (recurringError) throw recurringError

  const LABELS = ['jan','fev','mar','abr','mai','jun','jul','ago','set','out','nov','dez']
  const months: { income: number; expense: number }[] = Array.from({ length: 12 }, () => ({ income: 0, expense: 0 }))
  const recurringSources = [
    ...(data ?? []).filter(row => row.recurring),
    ...(recurringPrev ?? []),
  ]
  const cardCycles = await fetchCardCyclesForRows(householdId, recurringSources)

  for (const row of data ?? []) {
    const effectiveDate = row.card_id && row.due_date ? row.due_date : row.date
    const m = parseInt(effectiveDate.split('-')[1], 10) - 1
    if (row.kind === 'in')  months[m].income  += Number(row.amount)
    if (row.kind === 'out') months[m].expense += Number(row.amount)
  }

  for (const row of recurringSources) {
    const cycle = row.card_id ? cardCycles.get(row.card_id) : undefined
    const projected = projectRecurringRowInRange(row, from, to, cycle)
    for (const occurrence of projected) {
      const effectiveDate = occurrence.card_id && occurrence.due_date ? occurrence.due_date : occurrence.date
      const m = parseInt(effectiveDate.split('-')[1], 10) - 1
      if (occurrence.kind === 'in')  months[m].income  += Number(occurrence.amount)
      if (occurrence.kind === 'out') months[m].expense += Number(occurrence.amount)
    }
  }

  return months.map((m, i) => ({ label: LABELS[i], ...m }))
}

export async function getAnnualCategoryBreakdown(
  householdId: string,
  year: number,
): Promise<AnnualCategoryRow[]> {
  const from = `${year}-01-01`
  const to   = `${year + 1}-01-01`

  const [{ data, error }, { data: recurringPrev, error: recurringError }] = await Promise.all([
    supabase
      .from('transactions')
      .select('kind, amount, date, due_date, card_id, recurring, recurring_until, categories!category_id(id, name, kind)')
      .eq('household_id', householdId)
      .or(effectiveDateFilter(from, to)),
    supabase
      .from('transactions')
      .select('kind, amount, date, card_id, due_date, recurring_until, categories!category_id(id, name, kind)')
      .eq('household_id', householdId)
      .eq('recurring', true)
      .lt('date', from),
  ])

  if (error) throw error
  if (recurringError) throw recurringError

  const map = new Map<string, { name: string; kind: 'in' | 'out'; values: number[] }>()
  const recurringSources = [
    ...(data ?? []).filter(row => row.recurring),
    ...(recurringPrev ?? []),
  ]
  const cardCycles = await fetchCardCyclesForRows(householdId, recurringSources)

  const getOrCreate = (id: string, name: string, kind: 'in' | 'out') => {
    if (!map.has(id)) map.set(id, { name, kind, values: Array(12).fill(0) })
    return map.get(id)!
  }

  for (const row of data ?? []) {
    const cat = row.categories as { id?: string; name?: string; kind?: string } | null
    if (!cat?.id || !cat.name) continue
    const entry = getOrCreate(cat.id, cat.name, (cat.kind ?? row.kind) as 'in' | 'out')
    const effectiveDate = row.card_id && row.due_date ? row.due_date : row.date
    const m = parseInt(effectiveDate.split('-')[1], 10) - 1
    entry.values[m] += Number(row.amount)
  }

  for (const row of recurringSources) {
    const cat = row.categories as { id?: string; name?: string; kind?: string } | null
    if (!cat?.id || !cat.name) continue
    const entry = getOrCreate(cat.id, cat.name, (cat.kind ?? row.kind) as 'in' | 'out')
    const cycle = row.card_id ? cardCycles.get(row.card_id) : undefined
    const projected = projectRecurringRowInRange(row, from, to, cycle)
    for (const occurrence of projected) {
      const effectiveDate = occurrence.card_id && occurrence.due_date ? occurrence.due_date : occurrence.date
      const m = parseInt(effectiveDate.split('-')[1], 10) - 1
      entry.values[m] += Number(occurrence.amount)
    }
  }

  return Array.from(map.entries())
    .map(([id, val]) => ({ id, ...val }))
    .sort((a, b) => {
      if (a.kind !== b.kind) return a.kind === 'in' ? -1 : 1
      return b.values.reduce((s, v) => s + v, 0) - a.values.reduce((s, v) => s + v, 0)
    })
}
